/**
 * Add Property Slide-over Alpine.js Component
 *
 * A create-only slideover for quickly adding new properties.
 * Follows the tenancy slideover create-mode pattern.
 */

export function addPropertySlideover() {
  return {
    open: false,
    saving: false,
    formErrors: {},
    addressDuplicate: null,
    zIndex: 100010,

    form: {
      postcode: '',
      house_number: '',
      street: '',
      town: '',
      property_type: '',
      beds: '',
      bathrooms: '',
      furnished: 'Unknown',

      // Landlord
      landlord_search: '',
      landlord_results: [],
      searching_landlords: false,
      show_landlord_dropdown: false,
      selected_landlord: null,
      show_new_landlord_form: false,
      new_landlord_duplicate: null,
      new_landlord: {
        landlord_type: 'individual',
        first_name: '',
        last_name: '',
        display_name: '',
        organisation_name: '',
        email: '',
        phone: ''
      }
    },

    init() {
      const self = this
      window.addEventListener('open-add-property-slideover', () => {
        self.openForCreate()
      })
    },

    openForCreate() {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.formErrors = {}
      this.addressDuplicate = null
      this.saving = false

      this.form = {
        postcode: '',
        house_number: '',
        street: '',
        town: '',
        property_type: '',
        beds: '',
        bathrooms: '',
        furnished: 'Unknown',
        landlord_search: '',
        landlord_results: [],
        searching_landlords: false,
        show_landlord_dropdown: false,
        selected_landlord: null,
        show_new_landlord_form: false,
        new_landlord: {
          landlord_type: 'individual',
          first_name: '',
          last_name: '',
          display_name: '',
          organisation_name: '',
          email: '',
          phone: ''
        }
      }

      this.open = true
    },

    close() {
      this.open = false
    },

    get headerTitle() {
      const parts = [this.form.house_number, this.form.street, this.form.town].filter(Boolean)
      if (parts.length > 0) return parts.join(', ')
      if (this.form.postcode) return this.form.postcode
      return 'New Property'
    },

    // Has the user entered enough to identify an address (postcode + a first line)?
    get canCheckAddressDuplicate() {
      if (!this.form.postcode.trim()) return false
      return !!(this.form.house_number.trim() || this.form.street.trim())
    },

    // Warn, before save, if a property at this address already exists.
    checkAddressDuplicate() {
      this.addressDuplicate = null
      if (!this.canCheckAddressDuplicate) return

      const params = new URLSearchParams({
        house_number: this.form.house_number,
        street: this.form.street,
        postcode: this.form.postcode
      })

      fetch(`/properties/check_duplicate_address.json?${params.toString()}`, {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(response => response.json())
        .then(data => {
          if (data.duplicate) this.addressDuplicate = data.property
        })
        .catch(error => {
          console.error('Address duplicate check error:', error)
        })
    },

    openExistingProperty() {
      const id = this.addressDuplicate?.id
      if (!id) return
      this.close()
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('open-property-slideover', {
          detail: { id }
        }))
      }, 300)
    },

    // Landlord search
    searchLandlords() {
      const query = this.form.landlord_search.trim()
      if (query.length < 2) {
        this.form.landlord_results = []
        this.form.show_landlord_dropdown = false
        return
      }

      this.form.searching_landlords = true

      fetch(`/landlords/search.json?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(response => response.json())
        .then(landlords => {
          this.form.landlord_results = landlords
          this.form.show_landlord_dropdown = true
          this.form.searching_landlords = false
        })
        .catch(error => {
          console.error('Landlord search error:', error)
          this.form.searching_landlords = false
        })
    },

    selectLandlord(landlord) {
      this.form.selected_landlord = { ...landlord, is_new: false }
      this.form.landlord_search = ''
      this.form.landlord_results = []
      this.form.show_landlord_dropdown = false
    },

    removeLandlord() {
      this.form.selected_landlord = null
    },

    showNewLandlordForm() {
      this.form.show_new_landlord_form = true
      this.form.new_landlord_duplicate = null
      this.form.new_landlord = {
        landlord_type: 'individual',
        first_name: '',
        last_name: '',
        display_name: '',
        organisation_name: '',
        email: '',
        phone: ''
      }
    },

    cancelNewLandlord() {
      this.form.show_new_landlord_form = false
      this.form.new_landlord_duplicate = null
    },

    // Has the user entered enough of the inline landlord to identify it?
    get canCheckNewLandlordDuplicate() {
      const nl = this.form.new_landlord
      if (!nl.email || !nl.email.trim()) return false
      if (nl.landlord_type === 'individual') return !!(nl.first_name.trim() && nl.last_name.trim())
      if (nl.landlord_type === 'joint') return !!nl.display_name.trim()
      if (nl.landlord_type === 'organisation') return !!nl.organisation_name.trim()
      return false
    },

    // Warn, before save, if the inline landlord matches an existing record.
    checkNewLandlordDuplicate() {
      this.form.new_landlord_duplicate = null
      if (!this.canCheckNewLandlordDuplicate) return

      const nl = this.form.new_landlord
      const params = new URLSearchParams({
        landlord_type: nl.landlord_type,
        first_name: nl.first_name,
        last_name: nl.last_name,
        display_name: nl.display_name,
        organisation_name: nl.organisation_name,
        email: nl.email
      })

      fetch(`/landlords/check_duplicate.json?${params.toString()}`, {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(response => response.json())
        .then(data => {
          if (data.duplicate) this.form.new_landlord_duplicate = data.landlord
        })
        .catch(error => {
          console.error('Landlord duplicate check error:', error)
        })
    },

    // Link the matched existing landlord instead of creating a duplicate.
    useExistingLandlord() {
      const existing = this.form.new_landlord_duplicate
      if (!existing) return
      this.selectLandlord(existing)
      this.form.show_new_landlord_form = false
      this.form.new_landlord_duplicate = null
    },

    addNewLandlord() {
      // Cannot create a landlord that duplicates an existing one by name + email.
      if (this.form.new_landlord_duplicate) return

      const nl = this.form.new_landlord
      let name = ''

      if (nl.landlord_type === 'individual') {
        if (!nl.first_name || !nl.last_name) {
          this.formErrors.new_landlord = 'First name and last name are required'
          return
        }
        name = `${nl.first_name} ${nl.last_name}`
      } else if (nl.landlord_type === 'joint') {
        if (!nl.display_name) {
          this.formErrors.new_landlord = 'Display name is required'
          return
        }
        name = nl.display_name
      } else if (nl.landlord_type === 'organisation') {
        if (!nl.organisation_name) {
          this.formErrors.new_landlord = 'Organisation name is required'
          return
        }
        name = nl.organisation_name
      }

      delete this.formErrors.new_landlord

      this.form.selected_landlord = {
        is_new: true,
        name: name,
        landlord_type: nl.landlord_type,
        email: nl.email,
        phone: nl.phone,
        first_name: nl.first_name,
        last_name: nl.last_name,
        display_name: nl.display_name,
        organisation_name: nl.organisation_name
      }

      this.form.show_new_landlord_form = false
    },

    getLandlordInitials() {
      const name = this.form.selected_landlord?.name || ''
      const parts = name.split(' ').filter(Boolean)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
      return '?'
    },

    validateForm() {
      this.formErrors = {}

      if (!this.form.postcode.trim()) {
        this.formErrors.postcode = 'Postcode is required'
      }

      if (Object.keys(this.formErrors).length > 0) {
        this.formErrors.base = 'Please fill in the required fields.'
        return false
      }
      return true
    },

    buildPayload() {
      const payload = {
        property: {
          postcode: this.form.postcode.trim(),
          house_number: this.form.house_number.trim(),
          street: this.form.street.trim(),
          town: this.form.town.trim(),
          property_type: this.form.property_type || null,
          beds: this.form.beds ? parseInt(this.form.beds) : null,
          bathrooms: this.form.bathrooms ? parseInt(this.form.bathrooms) : null,
          furnished: this.form.furnished
        }
      }

      const landlord = this.form.selected_landlord
      if (landlord) {
        if (landlord.is_new) {
          payload.new_landlord = {
            landlord_type: landlord.landlord_type,
            first_name: landlord.first_name,
            last_name: landlord.last_name,
            display_name: landlord.display_name,
            organisation_name: landlord.organisation_name,
            email: landlord.email,
            phone: landlord.phone
          }
        } else {
          payload.landlord_id = landlord.id
        }
      }

      return payload
    },

    async createProperty() {
      // Cannot create a property that duplicates an existing one by address.
      if (this.addressDuplicate) return
      if (!this.validateForm()) return

      this.saving = true
      this.formErrors = {}

      try {
        const response = await fetch('/properties.json', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(this.buildPayload())
        })

        const data = await response.json()

        if (response.ok && data.success) {
          window.dispatchEvent(new CustomEvent('property-created', {
            detail: { property: data.property }
          }))
          this.close()

          // Open the new property's detail slideover
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('open-property-slideover', {
              detail: { id: data.property.id }
            }))
          }, 300)
        } else {
          if (data.errors && typeof data.errors === 'object') {
            Object.entries(data.errors).forEach(([field, messages]) => {
              this.formErrors[field] = Array.isArray(messages) ? messages[0] : messages
            })
            // Always surface something — some errors are on fields with no input
            // (e.g. reference), which would otherwise fail silently.
            if (!this.formErrors.base) {
              this.formErrors.base = 'Could not create the property: ' + Object.values(data.errors).flat().join(', ')
            }
          } else {
            this.formErrors.base = 'Failed to create property'
          }
        }
      } catch (error) {
        console.error('Failed to create property:', error)
        this.formErrors.base = 'An unexpected error occurred. Please try again.'
      } finally {
        this.saving = false
      }
    }
  }
}
